import React, { FC, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { HashLink } from 'react-router-hash-link';

import useRouter from 'src/presentation/hooks/useRouter';
import useIsFeatureEnabled from 'src/presentation/hooks/useIsFeatureEnabled';
import {
  StyledNavLinkBase,
  StyledNavLink as NavLink,
} from '@ad-scripts/presentation/components/common/nav/nav_link_styles';
import { StyledLinkOutStroke } from 'src/presentation/components/AgentMenu';

interface AdminPageLink {
  className?: string;
  dataAid?: string;
  featureFlag?: string;
  isExternalUrl?: boolean;
  label: string;
  roleFeature?: string;
  to: any;
}

const StyledHashLink = styled(HashLink)`
  ${StyledNavLinkBase}
`;

const ExternalLink = styled.a`
  ${StyledNavLinkBase}
`;

const scrollToActiveNavItem = refContainer => {
  if (refContainer?.current?.scrollIntoView) {
    const position = refContainer.current.getBoundingClientRect();
    if (position.top + position.height >= window.innerHeight || position.top < 60) {
      refContainer.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }
};

export const AdminPageLink: FC<AdminPageLink> = ({
  className,
  dataAid = 'pageLink',
  featureFlag = null,
  isExternalUrl,
  label,
  roleFeature,
  to,
}) => {
  const refContainer = useRef(null);
  const { location } = useRouter();
  const isFeatureEnabled = useIsFeatureEnabled();
  const hasRole = roleFeature ? isFeatureEnabled(roleFeature) : true;
  const isEnabled = featureFlag ? isFeatureEnabled(featureFlag) : true;
  const isShown = hasRole && isEnabled;
  const isActive = location.pathname === to;
  useEffect(() => {
    if (isActive) {
      scrollToActiveNavItem(refContainer);
    }
  }, [isActive]);

  return isShown ? (
    <NavLink className={className} isActive={isActive} ref={isActive ? refContainer : null}>
      {isExternalUrl ? (
        <ExternalLink data-aid={dataAid} href={to} target="_blank" rel="noopener noreferrer">
          {label}
          <StyledLinkOutStroke />
        </ExternalLink>
      ) : (
        <StyledHashLink data-aid={dataAid} smooth to={to}>
          {label}
        </StyledHashLink>
      )}
    </NavLink>
  ) : null;
};
